"use client";

import { Box, Breadcrumbs, Link, Stack } from "@mui/material";
import { FontAwesomeIcon } from "@osn/react-components";
import { handleParentClick, type PageParent } from "./page-parent";

type ParticipantHeaderTitleProps = {
  parents?: PageParent[];
};

/**
 * Desktop header slot after the brand divider: the page's parent trail, every segment a link to its own route and the
 * trail ending in a chevron (`My Players ›`). The current page is never part of it — the page names itself once, with
 * `PageTitle` — so a single-level page leaves the slot empty and the selected navigation item names the page. Below
 * the laptop breakpoint the slot is omitted and `PageParentLink` carries the nearest parent above the title.
 */
export default function ParticipantHeaderTitle({ parents = [] }: ParticipantHeaderTitleProps) {
  if (!parents.length) {
    return null;
  }

  const chevron = (
    <Box component="span" sx={{ color: "text.disabled", display: "inline-flex" }}>
      <FontAwesomeIcon family="fa-solid" icon="fa-chevron-right" style={{ fontSize: 9 }} />
    </Box>
  );

  return (
    <Stack
      data-cy="participant-header-title"
      direction="row"
      sx={{
        alignItems: "center",
        color: "primary.contrastText",
        display: { mobile: "none", laptop: "flex" },
        gap: 2.5,
      }}
    >
      <Box sx={{ backgroundColor: "rgba(255, 255, 255, 0.2)", height: 24, width: "1px" }} />
      <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
        <Breadcrumbs
          aria-label="Parent pages"
          separator={chevron}
          sx={{ color: "inherit", "& .MuiBreadcrumbs-separator": { mx: 1 } }}
        >
          {parents.map((parent) => (
            <Link
              key={parent.href}
              href={parent.href}
              onClick={(event) => handleParentClick(event, parent)}
              underline="none"
              variant="groupLabel"
              sx={{
                borderBottom: "1px solid transparent",
                color: "inherit",
                lineHeight: 1,
                opacity: 0.76,
                pt: 0.25,
                "&:hover": { borderBottomColor: "golden.main", opacity: 1 },
              }}
            >
              {parent.label}
            </Link>
          ))}
        </Breadcrumbs>
        {chevron}
      </Stack>
    </Stack>
  );
}
